/*
 *
 * AdminPage / Growth / GlobalShares
 *
 * Global share-groups list + platform share metrics. Backed by
 * GET /api/v3/admin/share-groups (verified against
 * Api::V3::Admin::ShareGroupsController#index):
 *   { current_page, rows_per_page, page_count,
 *     data: [{ id, gif_id, created_at, share_count, success_count, fail_count,
 *              pending_count }],
 *     metrics: { daily_success_rate, weekly_success_rate, monthly_success_rate,
 *                all_time_success_rate, daily_execution_avg,
 *                monthly_execution_avg } }
 * The server pins per_page at 75; we page with the envelope's current_page /
 * page_count (it DOES return pagination metadata, unlike the device endpoint).
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import Table from "components/Table";
import Loader from "components/Loader";
import { useAdminShareGroups } from "../api/queries";
import {
  Wrapper,
  BackLink,
  MetricsGrid,
  Card,
  Metric,
  MetricLabel,
} from "./styledComponents";

// A success/execution rate may be a fraction (0–1) or null on a quiet DB.
const pct = (v) => (v == null ? "—" : `${Math.round(Number(v) * 100)}%`);
const secs = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}s`);
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

const columns = [
  { header: "Group", path: "id", width: "100px", noSort: true,
    customRow: (r) => `#${r.id}` },
  { header: "GIF", path: "gif_id", width: "100px", noSort: true,
    customRow: (r) => (r.gif_id != null ? `#${r.gif_id}` : "—") },
  { header: "Shares", path: "share_count", width: "90px", noSort: true,
    customRow: (r) => fmt(r.share_count) },
  { header: "Success", path: "success_count", width: "90px", noSort: true,
    customRow: (r) => fmt(r.success_count) },
  { header: "Failed", path: "fail_count", width: "90px", noSort: true,
    customRow: (r) => fmt(r.fail_count) },
  { header: "Pending", path: "pending_count", width: "90px", noSort: true,
    customRow: (r) => fmt(r.pending_count) },
];

const METRICS = [
  { key: "all_time_success_rate", label: "All-time success", fmt: pct },
  { key: "monthly_success_rate", label: "30-day success", fmt: pct },
  { key: "weekly_success_rate", label: "7-day success", fmt: pct },
  { key: "daily_success_rate", label: "Today success", fmt: pct },
  { key: "monthly_execution_avg", label: "30-day avg time", fmt: secs },
  { key: "daily_execution_avg", label: "Today avg time", fmt: secs },
];

const GlobalShares = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isFetching } = useAdminShareGroups({ page });

  if (isLoading) {
    return (
      <Wrapper>
        <h1>Global Shares</h1>
        <div data-testid="shares-loading">
          <Loader />
        </div>
      </Wrapper>
    );
  }

  if (isError || !data) {
    return (
      <Wrapper>
        <h1>Global Shares</h1>
        <p className="admin-growth__error">Couldn’t load share groups. Try again.</p>
      </Wrapper>
    );
  }

  const rows = data.data || [];
  const metrics = data.metrics || {};

  return (
    <Wrapper>
      <BackLink type="button" onClick={() => navigate("/admin/growth")}>
        ← Back to growth
      </BackLink>
      <h1>Global Shares</h1>

      <MetricsGrid>
        {METRICS.map((m) => (
          <Card key={m.key}>
            <Metric>{m.fmt(metrics[m.key])}</Metric>
            <MetricLabel>{m.label}</MetricLabel>
          </Card>
        ))}
      </MetricsGrid>

      {rows.length === 0 ? (
        <p className="admin-growth__empty">No share groups to show.</p>
      ) : (
        <Table
          columns={columns}
          data={rows}
          isFetching={isFetching}
          currentPage={data.current_page || page}
          pageCount={data.page_count || 1}
          onPageChange={setPage}
        />
      )}
    </Wrapper>
  );
};

export default GlobalShares;
